import React, { useCallback, useEffect, useMemo } from "react";

import { Box, Button, Stack, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useActionHook } from "hooks";
import { uniqBy } from "lodash";
import { useDealActions } from "store";
import { IDealRoundsWithInvestors } from "store/types";

import { CharacteristicData } from ".";

interface Props {
  type: "round" | "deal";
  characteristicData: CharacteristicData[];
  characteristicName: string;
  onSubmit: (characteristicData: CharacteristicData[]) => void;
  onCancel: () => void;
  dealId: number;
}

interface OrganizedData {
  dealLevel: CharacteristicData[];
  investorOnly: CharacteristicData[];
  rounds: Record<number, { roundData: CharacteristicData[]; investors: CharacteristicData[] }>;
}

const organizeCharacteristicData = (data: CharacteristicData[]): OrganizedData => {
  const dealLevel: CharacteristicData[] = [];
  const investorOnly: CharacteristicData[] = [];
  const rounds: Record<number, { roundData: CharacteristicData[]; investors: CharacteristicData[] }> = {};
  data.forEach((item) => {
    // deal characteristic
    if (!item.investor_id && !item.round_id && !item.is_deleted) {
      dealLevel.push(item);
      return;
    }

    // Investor-only: has investor_id but no round_id
    if (item.investor_id && !item.round_id && !item.is_deleted) {
      investorOnly.push(item);
      return;
    }

    // Round level: has round_id but no investor_id
    if (item.round_id && !item.investor_id && !item.is_deleted) {
      if (!rounds[item.round_id]) {
        rounds[item.round_id] = { roundData: [], investors: [] };
      }
      rounds[item.round_id].roundData.push(item);
      return;
    }

    // Round + Investor level: has both round_id and investor_id
    if (item.round_id && item.investor_id && !item.is_deleted) {
      if (!rounds[item.round_id]) {
        rounds[item.round_id] = { roundData: [], investors: [] };
      }
      rounds[item.round_id].investors.push(item);
    }
  });

  return { dealLevel, investorOnly, rounds };
};

export const OverrideContent: React.FC<Props> = ({
  type,
  characteristicData,
  characteristicName,
  onSubmit,
  onCancel,
  dealId
}) => {
  const { getDealRoundsWithInvestorsAction } = useDealActions();

  const organizedData = useMemo(() => {
    return organizeCharacteristicData(characteristicData) || null;
  }, [type, characteristicData]);

  const title = useMemo(() => {
    return type === "round" ? "Override all with Round Value" : "Override all with Deal Value";
  }, [type]);

  const {
    data: roundsWithInvestorsData,
    action: getDealRoundsWithInvestors,
    loading
  } = useActionHook(() => dealId && getDealRoundsWithInvestorsAction({ dealId }));

  const roundsWithInvestors: IDealRoundsWithInvestors[] = useMemo(() => {
    return Array.isArray(roundsWithInvestorsData) ? roundsWithInvestorsData : [];
  }, [roundsWithInvestorsData]);

  const investors = useMemo(() => {
    return uniqBy(
      roundsWithInvestors.flatMap((round) => round.investors),
      "id"
    );
  }, [roundsWithInvestors]);

  const getRoundName = useCallback(
    (roundId: string | number) => {
      return roundsWithInvestors.find((round) => round.round_id === Number(roundId))?.round_name || "";
    },
    [roundsWithInvestors]
  );

  const getInvestorName = useCallback(
    (investorId: string | number) => {
      return investors.find((investor) => investor.id === investorId)?.name || "";
    },
    [investors]
  );

  useEffect(() => {
    if (dealId) {
      getDealRoundsWithInvestors();
    }
  }, []);

  return (
    <Stack direction="column" gap="16px" sx={{ width: "520px", padding: "24px" }}>
      <Typography sx={{ fontSize: "18px", fontWeight: 400, color: "#071F35", lineHeight: "24px" }}>{title}</Typography>
      <AsyncContentMui isLoading={loading}>
        <Stack gap="20px">
          <Stack direction="column" sx={{ maxHeight: "400px", overflow: "auto" }}>
            {organizedData && Boolean(organizedData.dealLevel.length) && (
              <Stack direction="row" alignItems="center">
                <Typography noWrap sx={{ fontSize: "16px", fontWeight: 600, color: "#071F35", lineHeight: "24px" }}>
                  {characteristicName} (Deal): {organizedData.dealLevel.map((item) => item.value).join(", ")}
                </Typography>
              </Stack>
            )}
            {Object.entries(organizedData.rounds).map(([roundId, roundGroup]) => (
              <Stack
                key={roundId}
                direction="column"
                sx={{
                  ...(organizedData.dealLevel.length && { paddingLeft: "10px" })
                }}
              >
                {Boolean(roundGroup.roundData.length) && (
                  <Stack>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "16px",
                        fontWeight: organizedData.dealLevel.length ? 400 : 600,
                        color: "#071F35",
                        lineHeight: "24px"
                      }}
                    >
                      {Boolean(organizedData.dealLevel.length) && "• "} {characteristicName} ({getRoundName(roundId)}):{" "}
                      {roundGroup.roundData.map((item) => item.value).join(", ")}
                    </Typography>
                  </Stack>
                )}
                <Stack sx={{ pl: "16px" }}>
                  {roundGroup.investors.map((investor) => (
                    <Stack key={investor.id}>
                      <Typography
                        noWrap
                        sx={{ fontSize: "16px", fontWeight: 400, color: "#071F35", lineHeight: "24px" }}
                      >
                        • {characteristicName} ({getInvestorName(investor.investor_id || "")}): {investor.value || ""}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))}
            {Boolean(organizedData.investorOnly.length) && (
              <Stack direction="column">
                {organizedData.investorOnly.map((investor) => (
                  <Stack key={investor.id}>
                    <Typography noWrap sx={{ fontSize: "16px", fontWeight: 400, color: "#071F35", lineHeight: "24px" }}>
                      • {characteristicName} ({getInvestorName(investor.investor_id || "")}): {investor.value || ""}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </AsyncContentMui>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onCancel} sx={{ marginRight: "20px" }} variant="outlined">
          Close
        </Button>
        <Button disabled={loading} onClick={() => onSubmit(characteristicData)} variant="contained">
          Override
        </Button>
      </Box>
    </Stack>
  );
};
